import { LOCATION_RECENCY_BANDS } from '@pams/shared';
import { LOCATION_INTEL_COPY } from '../copy';
import { BAND_RADIUS, DOT_STROKE_WIDTH, type MapColours } from '../mapSpec';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL] WHAT A COLOUR MEANS — the legend, in the Map key dialog since [LOCATION-INTEL.1], and
 *  the note that a dot is a phone's last report, not the officer's position now.
 *
 *  ⚠️ THE SWATCH IS THE DOT: the same palette value and the same size the map paints (`mapSpec.ts`'s
 *  `BAND_RADIUS` and `DOT_STROKE_WIDTH`), so the legend cannot show a green the map does not draw. Mapbox
 *  draws the outline OUTSIDE the radius, and preflight makes this box `border-box`, so the swatch is
 *  `(radius + stroke) × 2` across with the outline inside it — [LOCATION-INTEL.1] found it 4 px small.
 *  It is `aria-hidden` — the words beside it are the content (`CoverageBar`'s R2), and they say
 *  "last reported", never "on duty".
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = LOCATION_INTEL_COPY;

export const Legend = ({ colours }: { colours: MapColours }) => (
  <section className="flex flex-col gap-3 text-sm" aria-labelledby="location-intel-legend-title">
    <h2 id="location-intel-legend-title" className="font-medium">
      {copy.legendTitle}
    </h2>
    <ul className="flex flex-col gap-2" data-testid="location-intel-legend">
      {LOCATION_RECENCY_BANDS.map((band) => (
        <li key={band} className="flex items-center gap-2" data-band={band}>
          <span className="flex size-5 shrink-0 items-center justify-center" aria-hidden>
            <span
              className="block rounded-full border-card"
              data-testid="location-intel-legend-swatch"
              style={{
                width: (BAND_RADIUS[band] + DOT_STROKE_WIDTH) * 2,
                height: (BAND_RADIUS[band] + DOT_STROKE_WIDTH) * 2,
                borderWidth: DOT_STROKE_WIDTH,
                backgroundColor: colours[band],
              }}
            />
          </span>
          <span>{copy.band[band]}</span>
        </li>
      ))}
    </ul>
    <p className="text-muted-foreground">{copy.legendCluster}</p>
    <p className="text-muted-foreground" data-testid="location-intel-legend-note">
      {copy.legendNote}
    </p>
  </section>
);
